import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-status-pill.css?inline';

/**
 * `<pk-status-pill>` — small inline status chip.
 *
 * Lighter-weight than reaching for `<pk-data-table>`'s chipStyles for a
 * one-off use. Renders the slotted label text with a status-driven background +
 * border. Optional `dot` attribute swaps to a status-light style (small leading
 * dot rather than a tinted background).
 *
 * Slots:
 *   - default — the label text.
 *
 * Attributes:
 *   - `kind` — `success` | `warn` | `danger` | `info` | `neutral`
 *              (default `neutral`).
 *   - `dot` — boolean; switches to the dot-with-text "status light" style.
 */
export class PkStatusPill extends HTMLElement {
    static readonly tagName = 'pk-status-pill';

    static get observedAttributes(): string[] {
        return ['kind', 'dot'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private _kind(): 'success' | 'warn' | 'danger' | 'info' | 'neutral' {
        const v = this.getAttribute('kind') ?? 'neutral';
        switch (v) {
            case 'success':
            case 'warn':
            case 'danger':
            case 'info':
                return v;
            default:
                return 'neutral';
        }
    }

    private template(): TemplateResult {
        const kind = this._kind();
        const dot  = this.hasAttribute('dot');
        return html`
            <style>${styles}</style>
            <span class="pill kind-${kind} ${dot ? 'dot' : ''}" role="status">
                ${dot ? html`<span class="dot-marker" aria-hidden="true"></span>` : ''}
                <span class="label"><slot></slot></span>
            </span>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkStatusPill.tagName)) {
    customElements.define(PkStatusPill.tagName, PkStatusPill);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-status-pill': PkStatusPill;
    }
}
